import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Award,
  Medal,
  Percent,
  RefreshCw,
  Search,
  Share2,
  Trophy,
  UserX,
} from "lucide-react";
import { fetchPublicSleeperLookup } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { fmtNum } from "@/lib/format";

const LOOKUP_RECENT_KEY = "trystomatic.lookup.recent";
const LOOKUP_LOADING_MSG = "Pulling leagues from Sleeper — active managers can take up to 30 seconds.";

const LEAGUE_FILTERS = [
  { id: "ALL", label: "ALL" },
  { id: "DYNASTY", label: "DYNASTY" },
  { id: "REDRAFT", label: "REDRAFT" },
];

const POS_FILTERS = [
  { id: "ALL", label: "ALL" },
  { id: "QB", label: "QB" },
  { id: "RB", label: "RB" },
  { id: "WR", label: "WR" },
  { id: "TE", label: "TE" },
];

const TABS = [
  { id: "overview", label: "Overview", path: "" },
  { id: "ownership", label: "Ownership", path: "ownership" },
  { id: "trades", label: "Trade History", path: "trades" },
  { id: "history", label: "League History", path: "history" },
];

function parseLookupLocation(pathname) {
  const p = pathname.replace(/\/+$/, "") || "/lookup";
  const parts = p.split("/").filter(Boolean);
  if (parts[0] !== "lookup") return { username: null, tab: "overview" };
  if (parts.length === 1) return { username: null, tab: "overview" };
  let username = parts[1];
  try {
    username = decodeURIComponent(username);
  } catch {
    /* keep raw */
  }
  const seg = parts[2];
  if (!seg) return { username, tab: "overview" };
  if (seg === "ownership") return { username, tab: "ownership" };
  if (seg === "trades") return { username, tab: "trades" };
  if (seg === "history") return { username, tab: "history" };
  return { username, tab: "overview" };
}

function buildLookupPath(username, tab) {
  if (!username?.trim()) return "/lookup";
  const u = encodeURIComponent(username.trim());
  const t = TABS.find((x) => x.id === tab);
  const suffix = t?.path ? `/${t.path}` : "";
  return `/lookup/${u}${suffix}`;
}

function sleeperAvatarUrl(avatar) {
  if (!avatar) return null;
  if (String(avatar).startsWith("http")) return avatar;
  return `https://sleepercdn.com/avatars/${avatar}`;
}

function formatTradeDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function leagueTypeLabel(t) {
  if (t === "dynasty") return "Dynasty";
  if (t === "keeper") return "Keeper";
  return "Redraft";
}

function matchesLeagueFilter(row, filter) {
  if (filter === "ALL") return true;
  if (filter === "DYNASTY") return row.league_type === "dynasty";
  if (filter === "REDRAFT") return row.league_type === "redraft" || row.league_type === "keeper";
  return true;
}

function readRecentSearches() {
  try {
    const raw = localStorage.getItem(LOOKUP_RECENT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string").slice(0, 5) : [];
  } catch {
    return [];
  }
}

function pushRecentSearch(username) {
  const u = (username || "").trim();
  if (!u) return;
  try {
    const prev = readRecentSearches();
    const next = [u, ...prev.filter((x) => x.toLowerCase() !== u.toLowerCase())].slice(0, 5);
    localStorage.setItem(LOOKUP_RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Cross-league portfolio (same idea as Track Trystomatic). */
function buildPortfolioOwnership(leagues) {
  const leagues_total = leagues.length;
  if (!leagues_total) return { rows: [], leagues_total: 0 };
  const byId = new Map();
  for (const lg of leagues) {
    const seenInLeague = new Set();
    for (const p of lg.players || []) {
      const id = String(p.sleeper_id ?? "").trim();
      if (!id || seenInLeague.has(id)) continue;
      seenInLeague.add(id);
      if (!byId.has(id)) {
        byId.set(id, {
          player_id: id,
          name: p.name || `Sleeper #${id}`,
          position: p.position || "—",
          team: p.team ?? null,
          count: 0,
        });
      }
      byId.get(id).count += 1;
    }
  }
  const rows = Array.from(byId.values()).map((v) => {
    const pct = Math.round((100 * v.count) / leagues_total);
    return {
      player_id: v.player_id,
      name: v.name,
      position: v.position,
      team: v.team,
      leagues_owned: v.count,
      leagues_total,
      pct,
    };
  });
  rows.sort((a, b) => {
    if (b.pct !== a.pct) return b.pct - a.pct;
    if (b.leagues_owned !== a.leagues_owned) return b.leagues_owned - a.leagues_owned;
    return a.name.localeCompare(b.name);
  });
  return { rows, leagues_total };
}

function computeHeaderStats(ownershipFiltered, finishingFiltered, tradesFiltered) {
  const leagueCount = ownershipFiltered.length;
  let wins = 0;
  let losses = 0;
  let ties = 0;
  for (const lg of ownershipFiltered) {
    wins += Number(lg.wins) || 0;
    losses += Number(lg.losses) || 0;
    ties += Number(lg.ties) || 0;
  }
  const places = finishingFiltered.map((r) => r.place).filter((p) => Number(p) > 0);
  const bestFinish = places.length ? Math.min(...places) : null;
  const avgFinish = places.length
    ? Math.round((places.reduce((a, b) => a + b, 0) / places.length) * 10) / 10
    : null;
  let tradeW = 0;
  let tradeL = 0;
  let tradeEven = 0;
  for (const tr of tradesFiltered) {
    if (tr.grade === "W") tradeW += 1;
    else if (tr.grade === "L") tradeL += 1;
    else tradeEven += 1;
  }
  return {
    leagueCount,
    wins,
    losses,
    ties,
    bestFinish,
    avgFinish,
    tradeW,
    tradeL,
    tradeEven,
  };
}

function ordinalPlace(n) {
  if (n == null || n <= 0) return "—";
  const j = n % 10;
  const k = n % 100;
  if (k >= 11 && k <= 13) return `${n}th`;
  if (j === 1) return `${n}st`;
  if (j === 2) return `${n}nd`;
  if (j === 3) return `${n}rd`;
  return `${n}th`;
}

function PlaceMedal({ place }) {
  if (place === 1) return <Trophy className="inline text-[#FACC15] shrink-0" size={18} title="1st" />;
  if (place === 2) return <Medal className="inline text-[#52525B] shrink-0" size={18} title="2nd" />;
  if (place === 3) return <Award className="inline text-[#B45309] shrink-0" size={18} title="3rd" />;
  return null;
}

function SkeletonPulse({ className = "" }) {
  return <div className={`animate-pulse rounded bg-[#E5E7EB] ${className}`} />;
}

function TabSkeleton({ message }) {
  return (
    <div className="space-y-4 pt-4">
      {message ? (
        <p className="text-sm text-[#52525B] font-mono-data text-center py-2">{message}</p>
      ) : null}
      <SkeletonPulse className="h-8 w-48 mx-auto" />
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2 border border-[#0A0A0A]/20 p-4 rounded">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonPulse key={i} className="h-9 w-full" />
          ))}
        </div>
        <div className="space-y-2 border border-[#0A0A0A]/20 p-4 rounded">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonPulse key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

function NoData({ title = "No data available" }) {
  return (
    <div className="border border-[#0A0A0A]/20 bg-white px-6 py-10 text-center text-sm text-[#52525B] font-mono-data">
      {title}
    </div>
  );
}

export function SleeperLookupPage({ embedded = false }) {
  const [activePath, setActivePath] = useState(() => window.location.pathname);
  const { username: pathUser, tab: pathTab } = useMemo(() => parseLookupLocation(activePath), [activePath]);

  const [input, setInput] = useState("");
  const [leagueFilter, setLeagueFilter] = useState("ALL");
  const [posFilter, setPosFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [fatalError, setFatalError] = useState(null);
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [recent, setRecent] = useState(() => readRecentSearches());
  const [copied, setCopied] = useState(false);

  const syncFromWindow = useCallback(() => {
    setActivePath(window.location.pathname);
  }, []);

  useEffect(() => {
    window.addEventListener("popstate", syncFromWindow);
    return () => window.removeEventListener("popstate", syncFromWindow);
  }, [syncFromWindow]);

  useEffect(() => {
    if (pathUser) setInput(pathUser);
  }, [pathUser]);

  const go = useCallback((path) => {
    window.history.pushState({}, "", path);
    setActivePath(path);
  }, []);

  const loadSeqRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const loadProfile = useCallback(async (username) => {
    if (!username?.trim()) {
      setData(null);
      setNotFound(false);
      setFatalError(null);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++loadSeqRef.current;

    setLoading(true);
    setNotFound(false);
    setFatalError(null);
    setShowAllTrades(false);
    setData(null);
    try {
      const d = await fetchPublicSleeperLookup(username.trim(), { signal: controller.signal });
      if (seq !== loadSeqRef.current) return;
      setData(d);
      pushRecentSearch(username.trim());
      setRecent(readRecentSearches());
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      setData(null);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404 && detail === "Sleeper user not found") setNotFound(true);
      else setFatalError(apiErrorMessage(e, { context: "lookup" }));
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pathUser) loadProfile(pathUser);
    else {
      setData(null);
      setNotFound(false);
      setFatalError(null);
    }
  }, [pathUser, loadProfile]);

  const finishingFiltered = useMemo(
    () => (data?.finishing || []).filter((row) => matchesLeagueFilter(row, leagueFilter)),
    [data?.finishing, leagueFilter],
  );

  const ownershipFiltered = useMemo(
    () => (data?.ownership || []).filter((row) => matchesLeagueFilter(row, leagueFilter)),
    [data?.ownership, leagueFilter],
  );

  const tradesFiltered = useMemo(
    () => (data?.trades || []).filter((row) => matchesLeagueFilter(row, leagueFilter)),
    [data?.trades, leagueFilter],
  );

  const portfolioOwnership = useMemo(
    () => buildPortfolioOwnership(ownershipFiltered),
    [ownershipFiltered],
  );

  const portfolioPosFiltered = useMemo(() => {
    const { rows, leagues_total } = portfolioOwnership;
    if (posFilter === "ALL") return { rows, leagues_total };
    const filtered = rows.filter((r) => (r.position || "—") === posFilter);
    return { rows: filtered, leagues_total };
  }, [portfolioOwnership, posFilter]);

  const headerStats = useMemo(
    () => computeHeaderStats(ownershipFiltered, finishingFiltered, tradesFiltered),
    [ownershipFiltered, finishingFiltered, tradesFiltered],
  );

  const recentFinishes = useMemo(() => {
    const rows = [...finishingFiltered].sort((a, b) => {
      if (Number(b.season) !== Number(a.season)) return Number(b.season) - Number(a.season);
      return (a.league_name || "").localeCompare(b.league_name || "");
    });
    return rows.slice(0, 12);
  }, [finishingFiltered]);

  const tradesVisible = showAllTrades ? tradesFiltered : tradesFiltered.slice(0, 10);

  const profile = data?.profile;
  const sectionErrors = data?.section_errors || {};

  const onSubmitSearch = (e) => {
    e.preventDefault();
    const u = input.trim();
    if (!u) return;
    go(buildLookupPath(u, "overview"));
  };

  const openRecent = (u) => {
    go(buildLookupPath(u, "overview"));
  };

  const setTab = (tabId) => {
    if (!pathUser) return;
    go(buildLookupPath(pathUser, tabId));
  };

  const shareProfile = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Copied!");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const retryFatal = () => {
    if (pathUser) loadProfile(pathUser);
  };

  const renderLeagueFilter = () => (
    <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-testid="lookup-league-filter">
      {LEAGUE_FILTERS.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => setLeagueFilter(f.id)}
          className={`px-4 py-2 text-[11px] uppercase tracking-widest font-semibold border transition-colors ${
            leagueFilter === f.id
              ? "border-[#7C3AED] bg-[#7C3AED]/15 text-[#7C3AED]"
              : "border-[#0A0A0A]/20 text-[#52525B] hover:border-[#52525B] hover:text-[#0A0A0A]"
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );

  const renderTabs = () => {
    if (!pathUser) return null;
    return (
      <div className="flex flex-wrap gap-1 border-b border-[#0A0A0A]/20 mt-6" data-testid="lookup-tabs">
        {TABS.map((t) => {
          const active = pathTab === t.id;
          const href = buildLookupPath(pathUser, t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => go(href)}
              className={`px-4 py-3 text-[11px] uppercase tracking-widest font-semibold border-b-2 -mb-px transition-colors ${
                active ? "border-[#7C3AED] text-[#7C3AED]" : "border-transparent text-[#52525B] hover:text-[#0A0A0A]"
              }`}
              data-testid={`lookup-tab-${t.id}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderOverview = () => {
    if (loading && pathUser) {
      return <TabSkeleton message={LOOKUP_LOADING_MSG} />;
    }
    const leftErr = null;
    const rightErr = null;
    return (
      <div className="grid lg:grid-cols-2 gap-6 pt-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Percent size={16} className="text-[#7C3AED]" />
            <h3 className="font-display font-black text-lg tracking-tight">Portfolio ownership</h3>
          </div>
          {leftErr ? (
            <div className="text-sm text-[#F87171] font-mono-data border border-[#7F1D1D]/50 p-4">{leftErr}</div>
          ) : portfolioOwnership.leagues_total === 0 ? (
            <NoData />
          ) : portfolioOwnership.rows.length === 0 ? (
            <NoData />
          ) : (
            <div className="overflow-x-auto max-h-[min(60vh,480px)] overflow-y-auto border border-[#0A0A0A]/20 rounded">
              <table className="w-full text-sm min-w-[360px]">
                <thead className="sticky top-0 bg-[#F9FAFB] z-10 border-b border-[#0A0A0A]/20 text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data text-left">
                  <tr>
                    <th className="py-2 px-3">Player</th>
                    <th className="py-2 px-3 text-right">Leagues</th>
                    <th className="py-2 px-3 text-right">Portfolio %</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioOwnership.rows.map((row) => (
                    <tr key={row.player_id} className="border-b border-[#0A0A0A]/10">
                      <td className="py-2 px-3 text-[#0A0A0A] font-medium">{row.name}</td>
                      <td className="py-2 px-3 text-right font-mono-data text-[#52525B] tabular-nums">
                        {row.leagues_owned} / {row.leagues_total}
                      </td>
                      <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#7C3AED] tabular-nums">{row.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={16} className="text-[#7C3AED]" />
            <h3 className="font-display font-black text-lg tracking-tight">Recent finishes</h3>
          </div>
          {rightErr ? (
            <div className="text-sm text-[#F87171] font-mono-data border border-[#7F1D1D]/50 p-4">{rightErr}</div>
          ) : recentFinishes.length === 0 ? (
            <NoData />
          ) : (
            <div className="space-y-2">
              {recentFinishes.map((row) => (
                <div
                  key={`${row.league_id}-${row.season}`}
                  className="flex items-start gap-3 border border-[#0A0A0A]/20 bg-white px-4 py-3 rounded"
                >
                  <div className="pt-0.5">
                    <PlaceMedal place={row.place} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A0A0A] truncate">{row.league_name}</div>
                    <div className="text-xs text-[#52525B] font-mono-data mt-0.5">
                      {row.season} · {leagueTypeLabel(row.league_type)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-black text-lg text-[#7C3AED]">{row.place_label}</div>
                    <div className="text-[10px] text-[#52525B] font-mono-data">of {row.total_teams}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderOwnershipTab = () => {
    if (loading && pathUser) {
      return (
        <div className="pt-6 space-y-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <SkeletonPulse key={i} className="h-10 w-full" />
          ))}
        </div>
      );
    }
    const { rows, leagues_total } = portfolioPosFiltered;
    return (
      <div className="pt-6">
        <div className="flex flex-wrap gap-2 mb-4" data-testid="lookup-pos-filter">
          {POS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setPosFilter(f.id)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${
                posFilter === f.id
                  ? "border-[#7C3AED] bg-[#7C3AED]/15 text-[#7C3AED]"
                  : "border-[#0A0A0A]/20 text-[#52525B] hover:text-[#0A0A0A]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {leagues_total === 0 ? (
          <NoData />
        ) : rows.length === 0 ? (
          <NoData />
        ) : (
          <div className="overflow-x-auto max-h-[min(75vh,640px)] overflow-y-auto border border-[#0A0A0A]/20 rounded">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="sticky top-0 bg-[#F9FAFB] z-10 border-b border-[#0A0A0A]/20 text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data text-left">
                <tr>
                  <th className="py-2 px-3">Player</th>
                  <th className="py-2 px-3">Pos</th>
                  <th className="py-2 px-3">NFL</th>
                  <th className="py-2 px-3 text-right">Leagues</th>
                  <th className="py-2 px-3 text-right">Portfolio %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.player_id} className="border-b border-[#0A0A0A]/10">
                    <td className="py-2 px-3 text-[#0A0A0A] font-medium">{row.name}</td>
                    <td className="py-2 px-3 font-mono-data text-[#52525B]">{row.position}</td>
                    <td className="py-2 px-3 font-mono-data text-[#52525B]">{row.team || "—"}</td>
                    <td className="py-2 px-3 text-right font-mono-data text-[#52525B] tabular-nums">
                      {row.leagues_owned} / {row.leagues_total}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#7C3AED] tabular-nums">{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderTradesTab = () => {
    if (sectionErrors.trades) {
      return (
        <div className="pt-6 text-sm text-[#F87171] font-mono-data border border-[#7F1D1D]/40 bg-[#1C0A0A] px-4 py-3 rounded">
          {sectionErrors.trades}
        </div>
      );
    }
    if (loading && pathUser) {
      return (
        <div className="pt-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <SkeletonPulse key={i} className="h-32 w-full" />
          ))}
        </div>
      );
    }
    if (tradesFiltered.length === 0) {
      return (
        <div className="pt-6">
          <NoData />
        </div>
      );
    }
    const gradeClass = (g) => {
      if (g === "W") return "text-[#22C55E] border-[#22C55E]/40 bg-[#14532D]/20";
      if (g === "L") return "text-[#F87171] border-[#F87171]/40 bg-[#7F1D1D]/20";
      return "text-[#52525B] border-[#0A0A0A]/20 bg-white";
    };
    const diffClass = (d) => {
      if (d > 0) return "text-[#22C55E]";
      if (d < 0) return "text-[#F87171]";
      return "text-[#52525B]";
    };
    return (
      <div className="pt-6 space-y-4">
        {tradesVisible.map((t) => (
          <div key={`${t.league_id}-${t.transaction_id}`} className="border border-[#0A0A0A]/20 bg-white rounded overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-[#0A0A0A]/20 bg-[#F9FAFB]">
              <div className="text-xs font-mono-data text-[#52525B]">{formatTradeDate(t.created)}</div>
              <div className="text-xs text-[#7C3AED] font-medium truncate max-w-[200px]">{t.league_name}</div>
              <div className={`text-xs font-mono-data font-black px-2 py-1 border rounded ${gradeClass(t.grade)}`}>{t.grade}</div>
              <div className={`text-sm font-mono-data font-bold tabular-nums ${diffClass(t.value_diff)}`}>
                {t.value_diff > 0 ? "+" : ""}
                {fmtNum(t.value_diff)}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#EF4444] mb-2">Gave</div>
                <ul className="space-y-1 text-[#52525B]">
                  {(t.gave_assets || []).length ? (
                    t.gave_assets.map((a, i) => (
                      <li key={`g-${i}-${a.name}`} className="font-mono-data text-xs">
                        <span className="text-[#0A0A0A] font-medium">{a.name}</span>
                        <span className="text-[#52525B] mx-1">·</span>
                        {a.position}
                        {a.team ? (
                          <>
                            <span className="text-[#52525B] mx-1">·</span>
                            {a.team}
                          </>
                        ) : null}
                        <span className="text-[#52525B] ml-2">({fmtNum(a.combinedValue)})</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-[#52525B]">—</li>
                  )}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#22C55E] mb-2">Received</div>
                <ul className="space-y-1 text-[#52525B]">
                  {(t.got_assets || []).length ? (
                    t.got_assets.map((a, i) => (
                      <li key={`r-${i}-${a.name}`} className="font-mono-data text-xs">
                        <span className="text-[#0A0A0A] font-medium">{a.name}</span>
                        <span className="text-[#52525B] mx-1">·</span>
                        {a.position}
                        {a.team ? (
                          <>
                            <span className="text-[#52525B] mx-1">·</span>
                            {a.team}
                          </>
                        ) : null}
                        <span className="text-[#52525B] ml-2">({fmtNum(a.combinedValue)})</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-[#52525B]">—</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        ))}
        {tradesFiltered.length > 10 && (
          <button
            type="button"
            onClick={() => setShowAllTrades((v) => !v)}
            className="w-full py-3 text-xs uppercase tracking-widest border border-[#0A0A0A]/20 text-[#7C3AED] hover:border-[#7C3AED] font-semibold rounded"
            data-testid="lookup-trades-toggle"
          >
            {showAllTrades ? "Show fewer" : "See all"}
          </button>
        )}
      </div>
    );
  };

  const renderHistoryTab = () => {
    if (loading && pathUser) {
      return (
        <div className="pt-6 space-y-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonPulse key={i} className="h-12 w-full" />
          ))}
        </div>
      );
    }
    const rows = [...finishingFiltered].sort((a, b) => {
      if (Number(b.season) !== Number(a.season)) return Number(b.season) - Number(a.season);
      return (a.league_name || "").localeCompare(b.league_name || "");
    });
    if (rows.length === 0) {
      return (
        <div className="pt-6">
          <NoData />
        </div>
      );
    }
    return (
      <div className="pt-6 overflow-x-auto border border-[#0A0A0A]/20 rounded max-h-[min(75vh,640px)] overflow-y-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="sticky top-0 bg-[#F9FAFB] z-10 border-b border-[#0A0A0A]/20 text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data text-left">
            <tr>
              <th className="py-2 px-3">League</th>
              <th className="py-2 px-3">Type</th>
              <th className="py-2 px-3">Season</th>
              <th className="py-2 px-3">Finish</th>
              <th className="py-2 px-3">Record</th>
              <th className="py-2 px-3 text-right">Points</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.league_id}-${row.season}`} className="border-b border-[#0A0A0A]/10">
                <td className="py-2 px-3 text-[#0A0A0A] font-medium max-w-[220px] truncate">{row.league_name}</td>
                <td className="py-2 px-3 text-[#52525B]">{leagueTypeLabel(row.league_type)}</td>
                <td className="py-2 px-3 font-mono-data text-[#52525B]">{row.season}</td>
                <td className="py-2 px-3">
                  <span className="text-[#7C3AED] font-semibold">{row.place_label}</span>
                  <span className="text-[#52525B] text-xs ml-1">/ {row.total_teams}</span>
                </td>
                <td className="py-2 px-3 font-mono-data text-[#52525B] tabular-nums">
                  {row.wins}-{row.losses}
                  {Number(row.ties) > 0 ? `-${row.ties}` : ""}
                </td>
                <td className="py-2 px-3 text-right font-mono-data tabular-nums text-[#52525B]">{fmtNum(row.fpts)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = () => {
    if (!pathUser) return null;
    if (fatalError) return null;
    switch (pathTab) {
      case "ownership":
        return renderOwnershipTab();
      case "trades":
        return renderTradesTab();
      case "history":
        return renderHistoryTab();
      default:
        return renderOverview();
    }
  };

  return (
    <section className={embedded ? "space-y-6" : "min-h-screen bg-white text-[#0A0A0A]"} data-testid="sleeper-lookup-page">
      {embedded ? (
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#0A0A0A]/15 pb-6">
          <div>
            <div className="label-eyebrow">Public tool</div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-1">Sleeper profile lookup</h2>
            <p className="text-sm text-[#52525B] mt-1">Search any username — leagues, rosters, and trades. No login.</p>
          </div>
        </div>
      ) : null}

      <form onSubmit={onSubmitSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={18} />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Sleeper username — Enter to search"
            className="w-full bg-white border border-[#0A0A0A]/30 focus:border-[#7C3AED] outline-none pl-10 pr-4 py-3 text-sm font-mono-data text-[#0A0A0A] placeholder:text-[#52525B]"
            data-testid="lookup-search-input"
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={!input.trim()}
          className="px-5 py-3 text-xs uppercase tracking-widest font-semibold border border-[#0A0A0A] bg-[#7C3AED] text-white hover:bg-[#0A0A0A] rounded disabled:opacity-40"
          data-testid="lookup-search-submit"
        >
          Search
        </button>
      </form>

      {recent.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="lookup-recent-searches">
          <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data">Recent</span>
          {recent.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => openRecent(u)}
              className="px-2.5 py-1 text-xs font-mono-data border border-[#0A0A0A]/20 rounded-full text-[#7C3AED] hover:border-[#7C3AED] hover:bg-[#7C3AED]/10"
            >
              @{u}
            </button>
          ))}
        </div>
      )}

      <div className={embedded ? "space-y-6" : "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-6"}>
        {fatalError && pathUser && (
          <div className="mt-8 border border-[#7F1D1D] bg-[#1C0A0A] p-8 text-center rounded">
            <p className="text-[#FCA5A5] font-mono-data text-sm mb-4">{fatalError}</p>
            <button
              type="button"
              onClick={retryFatal}
              className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-semibold border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded"
            >
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        )}

        {!fatalError && notFound && pathUser && (
          <div className="mt-8 border border-[#0A0A0A]/20 bg-[#F9FAFB] p-10 text-center rounded shadow-[8px_8px_0_0_#000]" data-testid="lookup-not-found">
            <UserX className="mx-auto text-[#7C3AED]" size={40} />
            <h2 className="font-display font-black text-2xl mt-4 tracking-tight">User not found</h2>
            <p className="text-sm text-[#52525B] mt-2 font-mono-data">
              No Sleeper account matches <span className="text-[#0A0A0A]">@{pathUser}</span>.
            </p>
          </div>
        )}

        {!pathUser && !fatalError && (
          <div className={`${embedded ? "mt-6" : "mt-12"} text-center text-[#52525B] text-sm max-w-md mx-auto`}>
            {!embedded && (
              <p className="font-display font-black text-xl text-[#0A0A0A] tracking-tight mb-2">Sleeper profile lookup</p>
            )}
            <p className="leading-relaxed">
              Search any username or open a direct link like{" "}
              <span className="text-[#7C3AED] font-mono-data">/lookup/trystonevjen</span>
            </p>
          </div>
        )}

        {pathUser && !fatalError && !notFound && (
          <>
            {loading && pathUser && (
              <p className="mt-6 text-center text-sm text-[#52525B] font-mono-data" data-testid="lookup-loading-msg">
                {LOOKUP_LOADING_MSG}
              </p>
            )}
            <section className="mt-6 brutal-card shadow-[8px_8px_0_0_#0A0A0A] overflow-hidden border border-[#0A0A0A] bg-white">
              <div className="px-4 sm:px-8 py-8 flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex gap-5 flex-1 min-w-0">
                  {loading && !profile ? (
                    <SkeletonPulse className="w-24 h-24 rounded-lg shrink-0" />
                  ) : sleeperAvatarUrl(profile?.avatar) ? (
                    <img
                      src={sleeperAvatarUrl(profile.avatar)}
                      alt=""
                      className="w-24 h-24 rounded-lg border border-[#0A0A0A]/20 object-cover shrink-0 bg-[#F9FAFB]"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg border border-[#0A0A0A]/20 bg-[#F9FAFB] flex items-center justify-center text-2xl font-display font-black text-[#52525B] shrink-0">
                      {String(profile?.display_name || profile?.username || "?").slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0">
                    {loading && !profile ? (
                      <div className="space-y-2">
                        <SkeletonPulse className="h-6 w-48" />
                        <SkeletonPulse className="h-4 w-32" />
                      </div>
                    ) : (
                      <>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#7C3AED] font-mono-data">Sleeper</div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter truncate">
                          {profile?.display_name || profile?.username}
                        </h1>
                        <p className="text-[#52525B] font-mono-data mt-1">
                          @{profile?.username}
                          <span className="text-[#52525B] mx-2">·</span>
                          <span className="text-[#52525B]">NFL {data?.season ?? "—"}</span>
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-stretch lg:items-end gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={shareProfile}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#0A0A0A]/20 hover:border-[#7C3AED] rounded"
                  >
                    <Share2 size={14} /> {copied ? "Copied!" : "Share"}
                  </button>
                </div>
              </div>
              <div className="px-4 sm:px-8 pb-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 border-t border-[#0A0A0A]/15 bg-[#F9FAFB]">
                {[
                  { label: "Leagues", v: loading && !data ? "—" : headerStats.leagueCount },
                  { label: "Record", v: loading && !data ? "—" : `${headerStats.wins}-${headerStats.losses}${headerStats.ties ? `-${headerStats.ties}` : ""}` },
                  { label: "Best finish", v: loading && !data ? "—" : headerStats.bestFinish != null ? ordinalPlace(headerStats.bestFinish) : "—" },
                  { label: "Avg finish", v: loading && !data ? "—" : headerStats.avgFinish ?? "—" },
                  {
                    label: "Trade record",
                    v: loading && !data ? "—" : `${headerStats.tradeW}W / ${headerStats.tradeL}L / ${headerStats.tradeEven} Even`,
                  },
                ].map((s) => (
                  <div key={s.label} className="border border-[#0A0A0A]/20 px-3 py-2 bg-white">
                    <div className="text-[9px] uppercase tracking-widest text-[#52525B] font-mono-data">{s.label}</div>
                    <div className="font-display font-black text-lg tabular-nums text-[#0A0A0A] mt-0.5 break-words leading-tight text-sm sm:text-lg">{s.v}</div>
                  </div>
                ))}
              </div>
              {data?.truncated && (
                <p className="px-4 sm:px-8 py-3 text-xs text-[#92400E] font-mono-data border-t border-[#0A0A0A]/15">
                  Showing a capped subset of leagues — this user has more leagues than we load in one request.
                </p>
              )}
            </section>

            <div className="mt-6">{renderLeagueFilter()}</div>
            {renderTabs()}
            {renderTabContent()}

            {profile && data?.updated_at && (
              <p className="text-[10px] text-[#52525B] font-mono-data text-center mt-10">
                Data from Sleeper · trades from the last 12 months · TRYSTOMATIC values ·{" "}
                {new Date(data.updated_at).toLocaleString()}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
